import { ContentContainer } from "@/components/ContentContainer";
import EventEntry from "@/components/event/EventEntry";
import EventFilter from "@/components/event/EventFilter";
import EventNoEventsFound from "@/components/event/EventNoEventsFound";
import PaginationNavigation from "@/components/PaginationNavigation";
import { Spinner } from "@/components/Spinner";
import { useEvents } from "@/lib/api";
import { basierCirleBold, basierCirleRegular } from "@/styles/fonts";
import { Divider, Grid } from "@mui/material";
import { grey } from "@mui/material/colors";
import Typography from "@mui/material/Typography";
import { useState } from "react";

const Events = () => {
  const getLastDayOfThirdMonth = (date) => {
    const baseDate = date || new Date();
    return new Date(baseDate.getFullYear(), baseDate.getMonth() + 4, 0);
  };

  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: getLastDayOfThirdMonth(),
  });
  const [filter, setFilter] = useState({
    genre: "",
    region: "",
    zipcode: "",
    radius: "",
    date: dateRange.startDate,
  });
  const { events, isLoading, error } = useEvents(
    dateRange.startDate,
    dateRange.endDate,
    filter.genre,
    filter.region,
    filter.zipcode,
    filter.radius,
  );

  const handleFilterChanged = (name, value) => {
    if (name === "date_start") {
      setDateRange({
        startDate: value ?? new Date(),
        endDate: getLastDayOfThirdMonth(value ?? new Date()),
      });
    } else {
      setFilter({ ...filter, [name]: value });
    }
  };

  const prevPage = () => {
    const newStartMonth = dateRange.startDate.getMonth() - 3;
    const newEndMonth = dateRange.endDate.getMonth() - 3;

    setDateRange({
      startDate: new Date(dateRange.startDate.getFullYear(), newStartMonth, 1),
      endDate: new Date(dateRange.endDate.getFullYear(), newEndMonth + 1, 0),
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const nextPage = () => {
    const newStartMonth = dateRange.startDate.getMonth() + 3;
    const newEndMonth = dateRange.endDate.getMonth() + 3;

    setDateRange({
      startDate: new Date(dateRange.startDate.getFullYear(), newStartMonth, 1),
      endDate: new Date(dateRange.endDate.getFullYear(), newEndMonth + 1, 0),
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (error) {
    return <Typography>Ein Fehler ist aufgetreten.</Typography>;
  }

  const groupEventsByYearAndMonth = (events) => {
    return (
      events?.reduce((acc, event) => {
        const date = new Date(event.date_start);
        const year = date.getFullYear();
        const month = date.getMonth();

        if (!acc[year]) {
          acc[year] = {};
        }
        if (!acc[year][month]) {
          acc[year][month] = [];
        }

        acc[year][month].push(event);
        return acc;
      }, {}) || {}
    );
  };

  const groupedEvents = groupEventsByYearAndMonth(events);

  const monthNames = [
    "Januar",
    "Februar",
    "März",
    "April",
    "Mai",
    "Juni",
    "Juli",
    "August",
    "September",
    "Oktober",
    "November",
    "Dezember",
  ];

  return (
    <ContentContainer headline="Events">
      <Grid container spacing={{ xs: 1, sm: 3 }}>
        <Grid item xs={12} md={4}>
          <EventFilter onChange={handleFilterChanged} filter={filter} />
        </Grid>
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h4">Events</Typography>
            </Grid>
            {isLoading ? (
              <Grid item xs={12}>
                <Spinner />
              </Grid>
            ) : (
              <>
                {Object.keys(groupedEvents).length === 0 ? (
                  <Grid item xs={12}>
                    <EventNoEventsFound dateRange={dateRange} />
                  </Grid>
                ) : (
                  Object.entries(groupedEvents)
                    .sort(([yearA], [yearB]) => yearB - yearA)
                    .map(([year, months], yearIndex) => (
                      <Grid item xs={12} key={year}>
                        {yearIndex > 0 && <Divider>{year}</Divider>}
                        {Object.entries(months)
                          .sort(([monthA], [monthB]) => monthB - monthA)
                          .map(([month, monthEvents]) => (
                            <Grid item xs={12} key={`${year}-${month}`}>
                              <Typography sx={monthHeadlineStyles}>
                                {monthNames[month]}{" "}
                                <span className="month-count">
                                  ({monthEvents.length})
                                </span>
                              </Typography>
                              <EventEntry events={monthEvents} />
                            </Grid>
                          ))}
                      </Grid>
                    ))
                )}
              </>
            )}
          </Grid>
          <PaginationNavigation onPrevPage={prevPage} onNextPage={nextPage} />
        </Grid>
      </Grid>
    </ContentContainer>
  );
};

const monthHeadlineStyles = {
  fontSize: "1.125rem",
  mb: "1rem",
  textTransform: "uppercase",
  fontFamily: basierCirleBold.style.fontFamily,
  fontWeight: basierCirleBold.style.fontWeight,
  "& .month-count": {
    color: grey[600],
    fontFamily: basierCirleRegular.style.fontFamily,
    fontWeight: basierCirleRegular.style.fontWeight,
  },
};

export default Events;
